import * as Opt from "fp-ts/lib/Option";
import { censor } from "fp-ts/lib/Traced";
import { Attributes, Element } from "xml-js";

import { Funding, Soundbite, TranscriptLink, TranscriptLinks } from "./index";
import {
  attributeByNameOptional,
  channelElementsLens,
  elementByNameOptional,
  elementsOptional,
} from "./rssLenses";

export const getPIXmlns = (root: Element) =>
  Opt.getOrElse(() => "")(
    Opt.map((o) => "" + o)(
      elementsOptional
        .composeOptional(elementByNameOptional("rss"))
        .composeOptional(attributeByNameOptional("xmlns:podcast"))
        .getOption(root)
    )
  );

export const setPIXmlns = (xmlns: string, root: Element) =>
  elementsOptional
    .composeOptional(elementByNameOptional("rss"))
    .composeOptional(attributeByNameOptional("xmlns:podcast"))
    .set(xmlns)(root);

export const setFundingTags = (
  funding: Funding,
  rssFeedContent: Element
): Element => {
  return channelElementsLens.modify((elements: Element[]): Element[] => {
    // Remove existing funding tags
    const filteredElements = elements.filter(
      (element) => element?.name !== "podcast:funding"
    );

    // Add new ones
    const fundingTags = funding.map((f) => {
      return {
        attributes: { url: f.url },
        name: "podcast:funding",
        elements: [
          {
            type: "text",
            text: f.text,
          },
        ],
        type: "element",
      };
    });
    return [...filteredElements, ...fundingTags];
  })(rssFeedContent);
};

export const getChannelFunding = (feed: Element): Funding => {
  const fundElements: Element[] = Opt.getOrElse<Element[]>(() => [])(
    channelElementsLens.getOption(feed)
  );
  return fundElements
    .filter((e) => e.name === "podcast:funding")
    .map((el: Element) => {
      return {
        text: "" + (el.elements?.[0].text || ""),
        url: "" + (el.attributes?.["url"] || ""),
      };
    });
};

export const setItemValue = (mapper: (el: Element) => Element) => (
  guid: string
) => (feed: Element) => {
  return channelElementsLens.modify((channelElements: Element[]): Element[] => {
    const channelElements_ = channelElements.map((itemElement) => {
      const wantedItem = itemElement.elements?.some(
        (ge) =>
          ge.name === "guid" &&
          ge.elements?.some((gt) => gt.type === "text" && gt.text === guid)
      );
      // console.log("FOUND WANTED ITEM", wantedItem, itemElement);
      if (wantedItem) {
        return mapper(itemElement);
      } else {
        return itemElement;
      }
    });

    return channelElements_;
  })(feed);
};

export const setChapterLink = (url: string, mime: string) =>
  setItemValue((itemEl: Element) => {
    const found = itemEl.elements?.find(
      (element) => element?.name === "podcast:chapters"
    );

    const chaptersElement = {
      type: "element",
      name: "podcast:chapters",
      attributes: {
        url,
        type: mime,
      },
    };

    if (found) {
      return {
        ...itemEl,
        elements: itemEl?.elements?.map((el) =>
          el.name === "podcast:chapters" ? chaptersElement : el
        ),
      };
    } else {
      return {
        ...itemEl,
        elements: [...(itemEl?.elements || []), chaptersElement],
      };
    }
  });

export const setTranscriptLinks = (transcriptLinks: TranscriptLinks) =>
  setItemValue((itemEl: Element) => {
    const filteredItemElements =
      itemEl.elements?.filter((el) => el.name != "podcast:transcript") || [];

    const transcriptElements: Element[] = transcriptLinks.map((link) => ({
      type: "element",
      name: "podcast:transcript",
      attributes: getRidOfNulls(link),
    }));

    return {
      ...itemEl,
      elements: [...filteredItemElements, ...transcriptElements],
    };
  });

  export const setSoundbites = (soundbites: Soundbite[]) =>
  setItemValue((itemEl: Element) => {
    const filteredItemElements =
      itemEl.elements?.filter((el) => el.name != "podcast:soundbite") || [];

    const soundbiteElements: Element[] = soundbites.map((bite) => ({
      type: "element",
      name: "podcast:soundbite",
      elements: [{type:'text', text: bite.text}],
      attributes: {startTime: bite.start, duration: bite.duration},
    }));

    return {
      ...itemEl,
      elements: [...filteredItemElements, ...soundbiteElements],
    };
  });

export const getRidOfNulls = (link: TranscriptLink): Attributes => {
  const res = {
    url: link.url,
    type: link.mime,
    rel: link.rel || undefined,
    language: link.language || undefined,
  };
  return res;
};
